import React, { useState, useRef, useEffect } from "react";

const Spotify = () => {
  const [height, setHeight] = useState(360);
  const [isResizing, setIsResizing] = useState(false);
  const [actualIframeHeight, setActualIframeHeight] = useState(360);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const newHeight = e.clientY - rect.top;
      
      // Set minimum and maximum heights
      const minHeight = 360; // Match the iframe's minHeight
      const maxHeight = window.innerHeight - 200; // Leave some space for other elements
      
      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    const handleClickAway = (e) => {
      if (isResizing) {
        setIsResizing(false);
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('click', handleClickAway);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('click', handleClickAway);
    };
  }, [isResizing]);

  // Track iframe height changes
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        setActualIframeHeight(newHeight);
        // Update our height state to match the iframe's actual height
        if (!isResizing) {
          setHeight(newHeight);
        }
      }
    });

    resizeObserver.observe(iframe);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isResizing]);

  const styles = {
    page: {
      minHeight: "50vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 1rem",
      color: "#fff",
      textAlign: "center",
      position: "relative",
    },
    overlay: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "transparent",
      zIndex: 0,
    },
    content: {
      position: "relative",
      zIndex: 1,
      width: "100%",
      maxWidth: "720px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "0rem",
    },
    bannerImage: {
      width: "100%",
      opacity: 0.90,
      marginBottom: "0px", // super tight gap
    },
    embedContainer: {
      width: "100%",
      marginBottom: "1.5rem",
      position: "relative",
    },
    followButton: {
      display: "inline-block",
      padding: "0.75rem 1.5rem",
      background: "#1DB954",
      color: "#fff",
      borderRadius: "30px",
      fontWeight: "bold",
      textDecoration: "none",
      boxShadow: "0 0 3px #1DB954, 0 0 3px #1DB954",
      transition: "background 0.25s",
    },
    spotifyButtonLink: {
      display: "inline-block",
      marginBottom: "1rem",
    },
    spotifyButtonImage: {
      width: "240px",
      height: "auto",
    },
    drumLink: {
      display: "inline-block",
      marginTop: "1rem",
      textDecoration: "none",
      cursor: "pointer",
      userSelect: "none",
    },
    drumImage: {
      maxWidth: "50px",
      maxHeight: "50px",
      width: "100%",
      height: "auto",
      filter: "drop-shadow(0 0 1px #1ED760) drop-shadow(0 0 1px #1ED760)",
      transition: "transform 0.3s ease-in-out, filter 0.3s ease-in-out",
      animation: "pulseGlow 9s infinite ease-in-out",
    },
  };

  return (
    <div style={styles.page}>
      {/* SEO-friendly H1 for Spotify page */}
      <h1 style={{
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
        zIndex: -1
      }}>
        Drum Sircle on Spotify - Listen to Psychedelic Sounds
      </h1>
      
      <div style={styles.overlay}></div>
      <div style={styles.content}>
        <img
          src="/DrumSircleSounds2.png"
          alt="Drum Sircle Sounds"
          style={styles.bannerImage}
        />

        <div style={styles.embedContainer} ref={containerRef}>
          <div style={{ position: "relative", width: "100%", height: height }}>
            <iframe
              ref={iframeRef}
              src="https://open.spotify.com/embed/artist/24uo56EggPBjgfeyGExsMh?utm_source=generator&theme=0&viewMode=LIST"
              width="100%"
              height="100%"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="Drum Sircle Spotify Player"
              style={{ minHeight: "360px", opacity: 0.95 }}
            ></iframe>
            <div
              style={{
                position: "absolute",
                bottom: "0",
                right: "0",
                width: "0",
                height: "0",
                cursor: "se-resize",
                borderStyle: "solid",
                borderWidth: "0 0 15px 15px",
                borderColor: "transparent transparent #1ED760 transparent",
                zIndex: 10,
                transition: "all 0.1s ease-out",
                animation: "arrowColorPulse 3s infinite ease-in-out",
              }}
              onMouseDown={handleMouseDown}
              title="Drag to resize player"
            />
          </div>
        </div>

        <a
          href="https://open.spotify.com/artist/24uo56EggPBjgfeyGExsMh"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.spotifyButtonLink}
        >
          <img
            src="/spotifybutton.png"
            alt="Listen on Spotify"
            style={styles.spotifyButtonImage}
          />
        </a>

        <a href="/" style={styles.drumLink}>
          <img
            src="/drumsircleegg.png"
            alt="Drum Sircle Home"
            style={styles.drumImage}
          />
        </a>
      </div>

      <style>
        {`
          @keyframes pulseGlow {
            0% {
              filter: drop-shadow(0 0 1px #1ED760) drop-shadow(0 0 2.5px #1ED760);
            }
            50% {
              filter: drop-shadow(0 0 3px #1ED760) drop-shadow(0 0 7px #1ED760);
            }
            100% {
              filter: drop-shadow(0 0 1px #1ED760) drop-shadow(0 0 2.5px #1ED760);
            }
          }
          
          @keyframes arrowColorPulse {
            0% {
              border-color: transparent transparent #1ED760 transparent;
            }
            50% {
              border-color: transparent transparent #4ECDC4 transparent;
            }
            100% {
              border-color: transparent transparent #1ED760 transparent;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Spotify;
